
import { defineComponent, PropType } from 'vue'
import { mapState } from 'vuex'

import InstallAutofixMixin from '~/mixins/installAutofixMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'

import {
  buildAnalyzerMetaDefaultsForProps,
  parseAnalyzerTemplate
} from '@/utils/analyzerMetaDefaults'
import { toSentenceCase } from '@/utils/string'

import {
  AnalyzerMetaInterface,
  AnalyzerMetaProperitiesInterface,
  TransformerInterface
} from '~/store/analyzer/list'
import { AnalyzerAnalyzerType, Repository, TransformerTool } from '~/types/types'

import UIActionButton from '~/components/Common/UI2/Buttons/UIActionButton.vue'
import UIFilterCheckbox from '~/components/Common/UI2/Filter/UIFilterCheckbox.vue'
import UIRadioSelectors from '~/components/Common/UI2/Filter/UIRadioSelectors.vue'
import UITag from '~/components/Common/UI2/Tag/UITag.vue'

export default defineComponent({
  name: 'Analyzer',
  components: {
    UIRadioSelectors,
    UIFilterCheckbox,
    UIActionButton,
    UITag
  },
  mixins: [InstallAutofixMixin, RoleAccessMixin],
  props: {
    name: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    analyzerMeta: {
      type: Object as PropType<AnalyzerMetaInterface>,
      default: () => ({})
    },
    analyzerLogo: {
      type: String,
      default: ''
    },
    analyzerType: {
      type: String,
      default: ''
    },
    selectedAnalyzer: {
      type: Object as PropType<{
        name: string
        enabled: boolean
        meta: Record<string, unknown>
      }>,
      default: () => ({ name: '', enabled: false, meta: {} })
    },
    availableTransformers: {
      type: Array as PropType<Array<TransformerTool>>,
      default: () => []
    },
    selectedTransformers: {
      type: Array as PropType<Array<TransformerInterface>>,
      default: () => []
    },
    forTemplate: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    disableActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      configItems: [] as Array<AnalyzerMetaProperitiesInterface>,
      advancedConfigItems: [] as Array<AnalyzerMetaProperitiesInterface>,
      transformerItems: [] as Array<TransformerInterface>,
      invalidFields: [] as Array<string>,
      isCollapsed: true,
      isAdvancedCollapsed: true,
      hasUserInteracted: false,
      AnalyzerAnalyzerType
    }
  },
  created() {
    this.generateConfigItems()
    this.generateTransformerItems()
  },
  watch: {
    readOnly: {
      handler: 'updateIsCollapseOnReadStatusChange'
    },
    'selectedTransformers.length': {
      handler: 'updateTransformerItems'
    },
    configItems: {
      deep: true,
      handler: 'analyzersUpdated'
    },
    advancedConfigItems: {
      deep: true,
      handler: 'analyzersUpdated'
    },
    transformerItems: {
      deep: true,
      handler: 'transformersUpdated'
    }
  },
  computed: {
    ...mapState('repository/detail', {
      repository: (state: any) => state.repository as Repository
    }),
    hasCollapsibleContent(): boolean {
      return (
        this.configItems.length > 0 ||
        this.advancedConfigItems.length > 0 ||
        this.showTransformers ||
        this.showInstallAutofixAppCTA
      )
    },
    properties(): AnalyzerMetaInterface['properties'] {
      return this.analyzerMeta.properties
    },
    showInstallAutofixAppCTA(): boolean {
      // Show the CTA to install Autofix app only on code-review settings page
      if (this.$route.name !== 'provider-owner-repo-settings-code-review') {
        return false
      }

      return (
        this.availableTransformers.length > 0 &&
        this.allowAutofix &&
        !this.repository.isAutofixEnabled
      )
    },
    showTransformers(): boolean {
      if (this.forTemplate) {
        return this.availableTransformers.length > 0
      }

      return Boolean(this.repository.isAutofixEnabled && this.availableTransformers.length > 0)
    }
  },
  methods: {
    toSentenceCase,
    /**
     * Get radio options for UIRadioSelectors from config enum
     */
    getRadioOptions(
      config: AnalyzerMetaProperitiesInterface
    ): { label: string; value: string; description: string }[] {
      return (config.enum || []).map((option) => ({
        label: config.labels ? config.labels[option] : option,
        value: option,
        description: ''
      }))
    },
    /**
     * Get radio options with a None option prepended
     */
    getRadioOptionsWithNone(
      config: AnalyzerMetaProperitiesInterface
    ): { label: string; value: string; description: string }[] {
      return [{ label: 'None', value: '', description: '' }, ...this.getRadioOptions(config)]
    },
    /**
     * Get string value for boolean radio selectors
     */
    getBooleanRadioValue(
      selected: string | number | boolean | string[] | number[] | undefined
    ): string {
      if (selected === undefined || selected === '') return ''
      return `${Number(selected)}`
    },
    /**
     * Set boolean value from radio selector string
     */
    setBooleanValue(config: AnalyzerMetaProperitiesInterface, val: string): void {
      this.markUserInteracted()
      // Use empty string for 'None' instead of undefined to match the type
      if (val === '') {
        config.selected = ''
      } else {
        config.selected = Boolean(Number(val))
      }
    },
    markUserInteracted(): void {
      if (!this.hasUserInteracted) {
        this.hasUserInteracted = true
      }
    },
    onConfigValueSelected(config: AnalyzerMetaProperitiesInterface, value: string): void {
      this.markUserInteracted()
      config.selected = value
    },
    /**
     * Update collapse state based on the value for readOnly prop
     *
     * @returns {void}
     */
    updateIsCollapseOnReadStatusChange(): void {
      if (this.readOnly) {
        this.isCollapsed = false
      }
    },
    /**
     * Toggle transformer enabled state based on the value for readOnly prop
     *
     * @returns {void}
     */
    toggleTransformer(transformer: TransformerInterface) {
      if (this.readOnly || this.disableActions) {
        return
      }

      transformer.enabled = !transformer.enabled
    },
    /**
     * Discards Code Formatters and emit `onClose` event
     *
     * @returns {void}
     */
    onClose(): void {
      // remove all transformers
      this.removeAllTransformers()
      this.$emit('onClose')
    },
    /**
     * Replace template with actual/placeholder data
     *
     * @param {string} candidate
     * @returns {string}
     */
    parseTemplate(candidate: string): string {
      const { owner, repo, provider } = this.$route.params
      return parseAnalyzerTemplate(candidate, {
        provider,
        owner,
        repo,
        forTemplate: this.forTemplate,
        vcsHost: this.repository.vcsHost
      })
    },
    /**
     * Check if the config selected property has template data
     *
     * @param {AnalyzerMetaProperitiesInterface} config
     * @returns {boolean}
     */
    hasTemplate(config: AnalyzerMetaProperitiesInterface): boolean {
      if (typeof config.selected === 'string') {
        return (
          config.selected.includes('<%=  vcs_host %>') ||
          config.selected.includes('<%=  login %>') ||
          config.selected.includes('<%=  name %>')
        )
      }
      return false
    },
    /**
     * Build config items array from property names
     *
     * @param propNames - Array of property names to include
     * @param useDefaults - Whether to use default values when no saved value exists
     * @returns Array of config items
     */
    buildConfigItemsFromProps(
      propNames: string[],
      useDefaults = true
    ): AnalyzerMetaProperitiesInterface[] {
      const properties = this.analyzerMeta?.properties || {}

      return propNames
        .filter((name) => name in properties)
        .map((name) => {
          const config = { ...properties[name] }
          let selected

          if (this.selectedAnalyzer.enabled && this.selectedAnalyzer?.meta?.[name]) {
            selected = this.selectedAnalyzer.meta[name]

            if (typeof selected === 'string') {
              selected = this.parseTemplate(selected)
            }

            if (Array.isArray(selected) && selected.length) {
              selected = selected.map((item) =>
                typeof item === 'string' ? this.parseTemplate(item) : item
              )
            }
          }

          const defaults = buildAnalyzerMetaDefaultsForProps(
            this.analyzerMeta,
            {
              provider: this.$route.params.provider,
              owner: this.$route.params.owner,
              repo: this.$route.params.repo,
              forTemplate: this.forTemplate,
              vcsHost: this.repository.vcsHost
            },
            [name],
            useDefaults
          )

          const defaultValue = useDefaults ? defaults[name] : undefined

          if (defaultValue !== undefined) {
            config.default = defaultValue
          } else {
            delete (config as { default?: unknown }).default
          }

          return {
            ...config,
            name,
            // For advanced options, don't use defaults unless explicitly saved
            // For primary options, use defaults as fallback
            selected: selected !== undefined ? selected : useDefaults ? config.default : undefined
          } as AnalyzerMetaProperitiesInterface
        })
    },
    /**
     * Generate config items from analyzer meta properties
     * Separates properties into primary (required + optional_required) and advanced (purely optional)
     *
     * @returns {void}
     */
    generateConfigItems(): void {
      const requiredProps = this.analyzerMeta?.required || []
      const optionalRequiredProps = this.analyzerMeta?.optional_required || []
      const allProperties = this.analyzerMeta?.properties || {}

      // Primary props = required + optional_required (shown by default)
      const primaryProps = [...new Set([...requiredProps, ...optionalRequiredProps])]

      // Advanced props = all other properties (shown in collapsible section)
      const allPropertyNames = Object.keys(allProperties)
      const advancedProps = allPropertyNames.filter((name) => !primaryProps.includes(name))

      // Build config items for each category
      // Primary items use defaults, advanced items only show saved values
      this.configItems = this.buildConfigItemsFromProps(primaryProps, true)
      this.advancedConfigItems = this.buildConfigItemsFromProps(advancedProps, false)
    },
    /**
     * Generate Transformer items based on the selection
     *
     * @returns {void}
     */
    generateTransformerItems(): void {
      const selected = this.selectedTransformers
        .filter(({ enabled }) => enabled)
        .map(({ name }) => name)

      this.transformerItems = this.availableTransformers.map((transformer) => {
        return {
          ...transformer,
          enabled: selected.includes(transformer.shortcode)
        }
      })
    },
    /**
     * Update Code Formatter items when selectedTransformers list changes
     *
     * @returns {void}
     */
    updateTransformerItems(): void {
      this.generateTransformerItems()
    },
    /**
     * Update the value for selected key based on the value for isChecked
     *
     * @param {string} option
     * @param {number} isChcked
     * @param {Record<string, string[]>} objectToUpdate
     * @returns {void}
     */
    updateChecks(
      option: string | number,
      isChecked: number,
      objectToUpdate: AnalyzerMetaProperitiesInterface
    ): void {
      this.markUserInteracted()
      if (!objectToUpdate.selected) {
        objectToUpdate.selected = []
      }
      if (!Array.isArray(objectToUpdate.selected)) {
        return
      }

      const optionValue = String(option)

      if (isChecked) {
        ;(objectToUpdate.selected as (string | number)[]).push(option)
      } else {
        const index = objectToUpdate.selected.findIndex((value) => String(value) === optionValue)
        if (index !== -1) {
          objectToUpdate.selected.splice(index, 1)
        }
      }
    },
    onArrayOptionChange(
      option: string | number,
      isChecked: boolean,
      objectToUpdate: AnalyzerMetaProperitiesInterface
    ): void {
      this.updateChecks(option, isChecked ? 1 : 0, objectToUpdate)
    },
    /**
     * Update the value for selected key based on the value for isChecked
     *
     * @param {string} option
     * @param {Record<string, string[]>} objectToUpdate
     * @returns {void}
     */
    updateArray(option: string, objectToUpdate: AnalyzerMetaProperitiesInterface): void {
      this.markUserInteracted()
      objectToUpdate.selected = option
        .split(',') // comma separated
        .filter((val) => val) // non empty
        .map((val) => val.trim()) // no trailing or leading spaces
    },
    isArrayOptionSelected(
      config: AnalyzerMetaProperitiesInterface,
      option: string | number
    ): boolean {
      if (!Array.isArray(config.selected)) {
        return false
      }

      const selectedValues = config.selected.map((value) => String(value))
      return selectedValues.includes(String(option))
    },
    onArrayInput(value: string, objectToUpdate: AnalyzerMetaProperitiesInterface): void {
      this.updateArray(value, objectToUpdate)
    },
    normalizeConfigValue(
      configItem: AnalyzerMetaProperitiesInterface
    ): string | number | boolean | string[] | number[] | undefined {
      const selected = configItem.selected

      if (selected === undefined || selected === null || selected === '') {
        return undefined
      }

      if (configItem.type === 'integer') {
        const numericValue = Number(selected)
        return Number.isNaN(numericValue) ? selected : numericValue
      }

      if (
        configItem.type === 'array' &&
        configItem.items &&
        configItem.items.type === 'integer' &&
        Array.isArray(selected)
      ) {
        const stringValues = selected.map((item) => String(item))
        const numericValues = stringValues.map((item) => Number(item))
        const hasInvalidNumber = numericValues.some((item) => Number.isNaN(item))

        return hasInvalidNumber ? stringValues : numericValues
      }

      return selected
    },
    analyzersUpdated(): void {
      if (!this.hasUserInteracted) {
        return
      }
      const meta: Record<string, string | number | boolean | string[] | number[]> =
        Object.create(null)

      // Include primary config items
      this.configItems.forEach((configItem: AnalyzerMetaProperitiesInterface) => {
        const normalizedValue = this.normalizeConfigValue(configItem)
        if (normalizedValue !== undefined) {
          meta[configItem.name] = normalizedValue
        }
      })

      // Include advanced config items
      this.advancedConfigItems.forEach((configItem: AnalyzerMetaProperitiesInterface) => {
        const normalizedValue = this.normalizeConfigValue(configItem)
        if (normalizedValue !== undefined) {
          meta[configItem.name] = normalizedValue
        }
      })

      this.$emit('analyzersUpdated', {
        name: this.name,
        meta,
        enabled: true,
        type: this.analyzerType
      })
    },
    validateConfig(collapseCardsIfRequired = true): number {
      const requiredFields = this.analyzerMeta.required || []
      this.invalidFields = this.configItems
        .filter((configItem: AnalyzerMetaProperitiesInterface) => {
          const isEmptyValue =
            configItem.selected === undefined ||
            configItem.selected === null ||
            configItem.selected === '' ||
            (Array.isArray(configItem.selected) && configItem.selected.length === 0)

          return requiredFields.includes(configItem.name) && isEmptyValue
        })
        .map((configItem: AnalyzerMetaProperitiesInterface) => configItem.name)

      if (collapseCardsIfRequired) {
        this.isCollapsed = this.invalidFields.length === 0
      }

      return this.invalidFields.length
    },
    transformersUpdated(): void {
      const transformers: Record<string, Record<string, string | boolean>> = {}
      this.transformerItems.forEach((transformer) => {
        transformers[transformer.shortcode] = {
          name: transformer.shortcode,
          enabled: transformer.enabled
        }
      })

      this.$emit('transformersUpdated', transformers)
    },
    removeAllTransformers(): void {
      this.transformerItems.forEach((transformer) => {
        transformer.enabled = false
      })
      this.transformersUpdated()
    },
    toggleCollapsible(): void {
      this.isCollapsed = !this.isCollapsed
    },
    isRequired(name: string): boolean {
      return this.analyzerMeta.required?.includes(name) || false
    }
  }
})
